import { useCallback, useEffect, useRef, useState } from 'react'

interface UseAdProgressParams {
    getRemainingTime: () => number | null
    getDuration: () => number | null
}

export const useAdProgress = ({
    getRemainingTime,
    getDuration,
}: UseAdProgressParams) => {
    const timerRef = useRef<number | null>(null)
    const [progress, setProgress] = useState(0)
    const [elapsedSeconds, setElapsedSeconds] = useState(0)

    const clearProgressTimer = useCallback(() => {
        if (timerRef.current) {
            window.clearInterval(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const startProgressTimer = useCallback(() => {
        clearProgressTimer()
        timerRef.current = window.setInterval(() => {
            const duration = getDuration()
            const remaining = getRemainingTime()
            if (
                duration === null ||
                remaining === null ||
                !Number.isFinite(duration) ||
                !Number.isFinite(remaining) ||
                duration <= 0 ||
                remaining < 0 ||
                remaining > duration
            ) {
                return
            }
            const nextProgress = Math.min(
                1,
                Math.max(0, 1 - remaining / duration),
            )
            setProgress(nextProgress)
            setElapsedSeconds(Math.max(0, duration - remaining))
        }, 250)
    }, [clearProgressTimer, getDuration, getRemainingTime])

    const resetProgress = useCallback(() => {
        setProgress(0)
        setElapsedSeconds(0)
        clearProgressTimer()
    }, [clearProgressTimer])

    useEffect(() => {
        return () => {
            clearProgressTimer()
        }
    }, [clearProgressTimer])

    return {
        progress,
        elapsedSeconds,
        setElapsedSeconds,
        startProgressTimer,
        clearProgressTimer,
        resetProgress,
    }
}
